/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 9: My training › Overview. The app's Home on the web: level and
 * XP, today's workouts, the AI coach insight, the fitness profile and recent
 * activity. A radar area opens at ?axis= so Back returns here.
 */
import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Button, Card, CardContent, Skeleton } from "@lemonadetraining/web-ui";
import { IconChevronRight, IconPlus } from "@tabler/icons-react";
import {
  RADAR_AXES,
  RADAR_AXIS_DISPLAY_NAMES,
  type AICoachSuggestion,
  type MovementRegistryEntry,
  type RadarAxis,
  type RadarScores,
} from "@lemonadetraining/contracts";
import RadarChart from "../radar/RadarChart";
import AthleteCard from "../dashboard/AthleteCard";
import ActivityFeed from "../dashboard/ActivityFeed";
import AICoachCard from "../dashboard/AICoachCard";
import AxisDetailView from "../dashboard/AxisDetailView";
import { computeAxisDetail } from "../dashboard/axisDetailComputation";
import TodayWorkouts from "./TodayWorkouts";
import ClockNote from "../time/ClockNote";
import { openMovementSearch } from "../movement/movementSearchEvents";
import { useAthleteRadarProfile } from "../../hooks/useAthleteRadarProfile";
import RadarExplainerPopover from "../radar/RadarExplainerPopover";
import { useActivityFeed } from "../../hooks/useActivityFeed";
import { useTodaysWorkout } from "../../hooks/useTodaysWorkout";
import { useAICoachSuggestion } from "../../hooks/useAICoachSuggestion";
import { useBox } from "../../hooks/useBox";
import { useDisplayZone } from "../../hooks/useDisplayZone";
import { useMovementStateReadFlag } from "../../hooks/useMovementStateReadFlag";
import {
  combineStatus,
  profileRecordsByMovement,
  testedRecordsFromState,
} from "../../lib/movementStateRecords";
import type { UseMovementStateResult } from "../../hooks/useMovementState";
import { usableZone } from "../../lib/gymTime";

const ZERO_SCORES: RadarScores = RADAR_AXES.reduce(
  (acc, axis) => ({ ...acc, [axis]: 0 }),
  {} as RadarScores
);

const isRadarAxis = (v: unknown): v is RadarAxis => RADAR_AXES.includes(v as RadarAxis);

interface UserState {
  user: {
    user: { uid: string; displayName: string; photoURL?: string | null } | null;
    userInfoData?: {
      gender?: "male" | "female" | "other" | null;
      bodyWeight?: number | null;
      boxID?: string | null;
    };
  };
}

export interface TrainingOverviewProps {
  movementState: Pick<UseMovementStateResult, "status" | "movements">;
  registry: { status: "loading" | "ready" | "error"; movements: MovementRegistryEntry[] };
}

export default function TrainingOverview({ movementState, registry }: TrainingOverviewProps) {
  const user = useSelector((s: UserState) => s.user.user);
  const userInfoData = useSelector((s: UserState) => s.user.userInfoData);
  const uid = user?.uid ?? null;
  const boxID = userInfoData?.boxID ?? null;

  // "Today" is dated on the clock the member chose; a failed gym read uses the device's.
  const gym = useBox(boxID);
  const display = useDisplayZone(gym.isLoading ? undefined : (gym.box?.settings?.timezone ?? null));
  const radar = useAthleteRadarProfile(uid);
  const feed = useActivityFeed(uid, 10);
  const today = useTodaysWorkout(uid, {
    enabled: !display.isLoading,
    gymZone: display.gymZone,
    displayZone: display.zone,
  });
  const coach = useAICoachSuggestion(uid);

  const [params, setParams] = useSearchParams();
  const axisParam = params.get("axis");
  const openAxis = isRadarAxis(axisParam) ? axisParam : null;
  const showAxis = useCallback(
    (axis: RadarAxis) => {
      const next = new URLSearchParams(params);
      next.set("axis", axis);
      setParams(next);
    },
    [params, setParams]
  );
  const closeAxis = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete("axis");
    setParams(next);
  }, [params, setParams]);

  const weights = combineStatus(registry.status, movementState.status);
  // A percentage load is prescribed from a tested max only, never an estimate;
  // unknown weights must not read as "no 1RM".
  const oneRepMaxes = useMemo(
    () =>
      weights === "ready"
        ? testedRecordsFromState(movementState.movements, registry.movements)
        : undefined,
    [weights, movementState.movements, registry.movements]
  );

  const flagMode = useMovementStateReadFlag(true);

  const axisDetail = useMemo(() => {
    if (openAxis === null || flagMode === "loading") return null;
    return computeAxisDetail(
      openAxis,
      radar.profile,
      { gender: userInfoData?.gender, bodyWeight: userInfoData?.bodyWeight },
      profileRecordsByMovement(movementState.movements, registry.movements, flagMode === "on"),
      registry.movements
    );
  }, [
    openAxis,
    flagMode,
    radar.profile,
    userInfoData?.gender,
    userInfoData?.bodyWeight,
    movementState.movements,
    registry.movements,
  ]);

  const strongestAxis = radar.profile?.strongestAxis ?? null;
  const onInsightCta = useCallback(
    (s: AICoachSuggestion) => {
      const target =
        s.ctaAction === "navigate_to_axis" && isRadarAxis(s.ctaPayload?.axis)
          ? s.ctaPayload.axis
          : strongestAxis;
      if (target) showAxis(target);
    },
    [showAxis, strongestAxis]
  );

  // A stale ?axis= for someone with no profile yet has nothing to show.
  const isNewAthlete = radar.status === "empty";
  useEffect(() => {
    if (openAxis && isNewAthlete) closeAxis();
  }, [openAxis, isNewAthlete, closeAxis]);

  if (openAxis) {
    return (
      <section
        data-testid="athlete-dashboard-axis-detail"
        aria-label={`${RADAR_AXIS_DISPLAY_NAMES[openAxis]} axis detail`}
      >
        {weights === "error" ? (
          <div
            className="flex flex-col items-start gap-2 rounded-xl border p-4"
            role="alert"
            data-testid="axis-detail-error"
          >
            <p className="text-sm font-medium">
              We couldn&apos;t load your {RADAR_AXIS_DISPLAY_NAMES[openAxis]} results
            </p>
            <p className="text-sm text-muted-foreground">
              Check your connection and try again. Your results are safe.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => window.location.reload()}>
                Try again
              </Button>
              <Button variant="ghost" onClick={closeAxis}>
                Back to overview
              </Button>
            </div>
          </div>
        ) : (
          <AxisDetailView
            axis={openAxis}
            profile={radar.profile}
            detail={weights === "ready" ? axisDetail : null}
            onBack={closeAxis}
            backHref="?"
          />
        )}
      </section>
    );
  }

  const scores = radar.profile?.scores ?? ZERO_SCORES;
  const overallScore = radar.profile?.overallScore;
  const zone = usableZone(display.zone ?? display.browserZone);
  const dateLabel = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date());

  return (
    <div
      data-testid="athlete-dashboard"
      className="grid w-full gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]"
    >
      <div className="flex min-w-0 flex-col gap-4" data-testid="dashboard-left-column">
        {radar.status === "loading" || radar.status === "idle" ? (
          <Skeleton className="h-32 w-full" data-testid="athlete-card-skeleton" />
        ) : radar.status === "error" ? (
          <Card data-testid="athlete-card-error" role="alert">
            <CardContent className="p-4 text-sm">
              We couldn&apos;t load your level and fitness profile. Refresh the page to try again.
              Nothing you logged is lost.
            </CardContent>
          </Card>
        ) : (
          <AthleteCard
            displayName={user?.displayName ?? "Athlete"}
            photoURL={user?.photoURL}
            profile={radar.profile}
            isNewAthlete={isNewAthlete}
          />
        )}
        {isNewAthlete && (
          <Card data-testid="dashboard-empty-state">
            <CardContent className="flex flex-col items-start gap-2 p-4">
              <p className="font-medium">Log your first result</p>
              <p className="text-sm text-muted-foreground">
                Search a movement you did, open it, and log a number. Your records and your fitness
                profile start from there.
              </p>
              <Button
                onClick={() => openMovementSearch({ log: true })}
                data-testid="dashboard-empty-state-cta"
              >
                <IconPlus aria-hidden="true" /> Log a result
              </Button>
            </CardContent>
          </Card>
        )}
        <TodayWorkouts
          status={today.status}
          programs={today.programs}
          partial={today.partial}
          onRetry={() => void today.refetch()}
          oneRepMaxes={oneRepMaxes}
          dateLabel={dateLabel}
          weights={weights}
          note={
            <ClockNote
              variant="link"
              mode={display.mode}
              city={display.city}
              differs={!display.isLoading && display.differs([Date.now()])}
              className="self-start sm:min-h-6"
            />
          }
        />
      </div>

      <div className="flex min-w-0 flex-col gap-4" data-testid="dashboard-right-column">
        {coach.suggestion && (
          <AICoachCard
            key={coach.suggestion.id}
            suggestion={coach.suggestion}
            onFeedback={coach.sendFeedback}
            onSeePlan={onInsightCta}
            onDismiss={coach.dismiss}
          />
        )}
        <Card data-testid="dashboard-center-column">
          <CardContent className="flex flex-col gap-3 p-4">
            <div>
              <div className="flex items-center gap-1">
                <h2 className="text-base font-semibold">Fitness profile</h2>
                {radar.status === "ready" && <RadarExplainerPopover subject="self" />}
              </div>
              <p className="text-sm text-muted-foreground">
                {isNewAthlete
                  ? "Fills in as you log results."
                  : radar.status === "error"
                    ? ""
                    : overallScore !== undefined
                      ? `Overall ${Math.round(overallScore)}. Pick an area to see what counts.`
                      : "Pick an area to see what counts."}
              </p>
            </div>
            {radar.status === "error" ? null : radar.status === "loading" ||
              radar.status === "idle" ? (
              <Skeleton
                className="mx-auto aspect-square w-full max-w-[320px] rounded-full"
                data-testid="radar-loading-skeleton"
              />
            ) : (
              <div className="flex w-full justify-center">
                <RadarChart
                  scores={isNewAthlete ? ZERO_SCORES : scores}
                  variant="dashboard"
                  onAxisClick={isNewAthlete ? undefined : showAxis}
                />
              </div>
            )}
            {isNewAthlete ? (
              <p
                className="text-center text-sm text-muted-foreground"
                data-testid="radar-empty-message"
              >
                Five areas: Strength, Olympic Power, Gymnastics, Endurance and Work Capacity. Your
                first result starts one of them.
              </p>
            ) : radar.status === "ready" ? (
              <ul className="flex flex-col" aria-label="Fitness profile areas">
                {RADAR_AXES.map((axis) => (
                  <li key={axis}>
                    <button
                      type="button"
                      onClick={() => showAxis(axis)}
                      className="flex min-h-11 w-full items-center gap-3 rounded-md px-2 text-left text-sm hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid={`axis-row-${axis}`}
                    >
                      <span className="flex-1">{RADAR_AXIS_DISPLAY_NAMES[axis]}</span>
                      <span className="font-medium tabular-nums">{Math.round(scores[axis])}</span>
                      <IconChevronRight
                        className="size-4 text-muted-foreground"
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {radar.status === "error" && (
              <p className="text-sm text-muted-foreground" data-testid="radar-error">
                Your fitness profile didn&apos;t load. Refresh the page to try again.
              </p>
            )}
          </CardContent>
        </Card>
        <ActivityFeed
          status={feed.status}
          items={feed.items}
          error={feed.error}
          hasMore={feed.hasMore}
          loadingMore={feed.loadingMore}
          loadMoreError={feed.loadMoreError}
          onLoadMore={feed.loadMore}
        />
      </div>
    </div>
  );
}
